import React, { useEffect, useState, useMemo } from 'react';
import { OrganizationCollection, User, CareCoordinatorReferral } from 'types';
import ListHeader from '../holders_list/components/ListHeader';
import EligibilityPopUp from '../holders_list/components/EligibilityComponents/PopUp';
import { createStyles, makeStyles } from '@mui/styles';
import { openPopUp, closePopUp } from 'redux/store/slices/eligibilitySlice';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { useMemberSearch } from './hooks/useMemberSearch';
import Control from './components/Controls/Control';
import SearchLoading from './components/Search Results/SearchLoading';
import SearchEmpty from './components/Search Results/SearchEmpty';
import SearchResults from './components/Search Results/SearchResults';
import AdminNavSelector from './components/AdminNavSelector';
import MemberGroupList from './components/Groups/MemberGroupList';
import MyMemberList from './components/MyMemberList';
import useOrganizationMemberGroup from './hooks/useOrganizationMemberGroup';
import EnrollmentProgress from 'components/holders_list/components/EnrollmentProgress';
import { canUserSignUp, shouldHideIntakeDeployment, SignUpAvailabilityResult } from 'util/user'; 
import { fetchActiveOrganizationCommitments } from 'api/commitments';
import useIntakeDeploymentOptions from 'hooks/api/useIntakeDeploymentOptions';

const useStyles = makeStyles(() =>
    createStyles({
        root: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            height: '100vh',
            overflow: 'auto',
            width: '100%',
            gap: '32px',
        },
        placeholderContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            flex: 1,
            borderRadius: '12px'
        },
        deploymentView: {
            backgroundColor: '#E3F2FD', // Light blue
            color: '#1976D2'
        },
        organizationView: {
            backgroundColor: '#FFEBEE', // Light red
            color: '#D32F2F'
        },
        placeholderText: {
            fontSize: '24px',
            fontWeight: 600,
            textAlign: 'center'
        }
    })
);

type OrgCollectionHomepageProps = {
    user: User;
    layoutTitles: { title: string; subtitle: string };
    setLayoutTitles: (title: string, subtitle: string) => void;
    resetCatchUpItemStatus: () => void;
    resetHomelessInfo: () => void;
    resetMessageInfo: () => void;
    resetMemberReferrals: () => void;
    resetTouchpointAnswers: () => void;
    resetUnenrollmentState: () => void;
    incomingCareCoordinatorReferrals: CareCoordinatorReferral[];
    fetchIncomingCareCoordinatorReferrals: (userID: number) => void;
    isFetchingIncomingCareCoordinatorReferrals: boolean;
    saveMemberToMyMembers: (caseloadID: number, memberID: number) => void;
    removeMemberFromMyMembers: (caseloadID: number, rejected?: boolean) => void;
}

const OrgCollectionHomepage: React.FC<OrgCollectionHomepageProps> = ({
    user,
    layoutTitles,
    setLayoutTitles,
    resetCatchUpItemStatus,
    resetHomelessInfo,
    resetMessageInfo,
    resetMemberReferrals,
    resetTouchpointAnswers,
    resetUnenrollmentState,
    incomingCareCoordinatorReferrals,
    fetchIncomingCareCoordinatorReferrals,
    isFetchingIncomingCareCoordinatorReferrals,
    saveMemberToMyMembers,
    removeMemberFromMyMembers
}) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { query, setQuery, results, isLoading } = useMemberSearch();
    const [activeView, setActiveView] = useState('My Members');
    const organizationHook = useOrganizationMemberGroup();

    const isEligibilityPopUpOpen = useSelector((state: any) => state.eligibility.isPopUpOpen);
    const isSearching = query.trim().length > 0;

    const [isFetchingCommitments, setIsFetchingCommitments] = useState(true);
    const [organizationCommitments, setOrganizationCommitments] = useState<any>(null);
    const [fetchCommitmentsError, setFetchCommitmentsError] = useState(false);
    const shouldShowSignupButton = useMemo(() => {
        return !isFetchingCommitments && (organizationCommitments?.commitments?.length === 0 || fetchCommitmentsError);
    }, [isFetchingCommitments, organizationCommitments, fetchCommitmentsError]);


    const { intakeDeploymentOptions, isFetchingIntakeDeploymentOptions, fetchIntakeDeploymentOptionsError } = useIntakeDeploymentOptions(user);
    const canSignUpMember = useMemo<SignUpAvailabilityResult>(() => {
        if (isFetchingIntakeDeploymentOptions || fetchIntakeDeploymentOptionsError) {
            return canUserSignUp(
                user,
                (user.partner?.deployments || []).filter(
                    (deployment) => !shouldHideIntakeDeployment(deployment)
                )
            );
        }
        return canUserSignUp(user, intakeDeploymentOptions);
    }, [isFetchingIntakeDeploymentOptions, intakeDeploymentOptions, fetchIntakeDeploymentOptionsError, user, user.partner?.deployments]);

    const [navItems, setNavItems] = useState(['My Members']);
    const [collections, setCollections] = useState<OrganizationCollection[]>([]);

    useEffect(() => {
        if (layoutTitles.subtitle !== 'Client List') {
            setLayoutTitles('', 'Client List');
        }

        resetCatchUpItemStatus();
        resetHomelessInfo();
        resetMessageInfo();
        resetMemberReferrals();
        resetTouchpointAnswers();
        resetUnenrollmentState();
    }, [
        layoutTitles.subtitle,
        setLayoutTitles,
        resetCatchUpItemStatus,
        resetHomelessInfo,
        resetMessageInfo,
        resetMemberReferrals,
        resetTouchpointAnswers,
        resetUnenrollmentState
    ]);

    useEffect(() => {
        const fetchCommitmentsData = async () => {
            if (!user?.id) return;
            setIsFetchingCommitments(true);
            setFetchCommitmentsError(false);
            try {
                const commitments = await fetchActiveOrganizationCommitments(user.partner?.id, user.id);
                setOrganizationCommitments(commitments);
            } catch (error) {
                console.error('Error fetching organization commitments:', error);
                setFetchCommitmentsError(true);
            } finally {
                setIsFetchingCommitments(false);
            }
        };

        fetchCommitmentsData();
    }, [user?.id, user.partner?.id]);

    useEffect(() => {
        const collections = user?.partner?.organization_collections;
        setCollections(collections);
        const names = ['My Members', ...collections.map((c) => c.name)];
        setNavItems(names);
    }, [user?.partner?.organization_collections]);



    const renderListView = () => {
        const isMyMemberView = activeView === 'My Members';
        
        if (isMyMemberView) {
            return <MyMemberList 
                incomingCareCoordinatorReferrals={incomingCareCoordinatorReferrals} 
                fetchIncomingCareCoordinatorReferrals={fetchIncomingCareCoordinatorReferrals}
                isFetchingIncomingCareCoordinatorReferrals={isFetchingIncomingCareCoordinatorReferrals}
                is_care_coordinator={user?.is_care_coordinator}
                user={user}
                saveMemberToMyMembers={saveMemberToMyMembers}
                removeMemberFromMyMembers={removeMemberFromMyMembers}
            />;
        }
        const selectedCollection = collections.find(
            (c) => c.name === activeView
        );

        if (selectedCollection) {
            return <MemberGroupList hook={organizationHook} collection={selectedCollection}/>;
        };
    };

    return (
        <div className={classes.root}>
            <Helmet title="Member List" />

            <ListHeader 
                    setEligibilityPopUp={() => dispatch(openPopUp())}
                    shouldShowSignupButton={shouldShowSignupButton}
                    shouldDisableSignupButton={!canSignUpMember.canSignUp}
            />
            {!shouldShowSignupButton && (
                <EnrollmentProgress
                    setEligibilityPopUp={() => dispatch(openPopUp())}
                    isLoading={isFetchingCommitments}
                    activeCommitments={organizationCommitments} 
                    canSignUpMember={canSignUpMember}
                />
            )}
            <Control 
                query={query}
                setQuery={setQuery}
            />
            {isSearching ? (
                isLoading ? (
                    <SearchLoading />
                ) : results.length > 0 ? (
                    <SearchResults members={results} />
                ) : (
                    <SearchEmpty />
                )
            ) : (
                <>
                    <AdminNavSelector
                        active={activeView}
                        setActive={setActiveView}
                        navItems={navItems}
                    />
                    {renderListView()}
                </>
            )}
            {isEligibilityPopUpOpen && (
                <EligibilityPopUp
                    handleCloseClick={() => dispatch(closePopUp())}
                    user={user}
                />
            )}
        </div>
    );
};

export default OrgCollectionHomepage; 